import { Link } from "react-router-dom";
import profile from "../data/profile";
import recipes from "../data/recipes";
import devPosts from "../data/devPosts";
import devHacks from "../data/devHacks";
import NewsletterSection from "../components/NewsletterSection";
import styles from "./AboutPage.module.css";

const SOCIAL_META = [
  { key: "github", label: "GitHub", emoji: "🐙" },
  { key: "linkedin", label: "LinkedIn", emoji: "💼" },
  { key: "instagram", label: "Instagram", emoji: "📸" },
];

export default function AboutPage() {
  return (
    <div>
      {/* ── HERO ── */}
      <div className={styles.hero}>
        <div className={styles.blob1} />
        <div className={styles.blob2} />

        <div className="container">
          <div className={styles.heroGrid}>
            {/* Avatar */}
            <div className={styles.avatarCol}>
              <div className={styles.avatarRing}>
                <div className={styles.avatar}>
                  <img
                    src={profile.avatar}
                    alt="profile"
                    className={styles.avatarImg}
                  />
                </div>
              </div>
              {profile.available && (
                <div className={styles.availBadge}>
                  <span className={styles.availDot} />
                  {profile.availableText}
                </div>
              )}
            </div>

            {/* Info */}
            <div className={styles.heroContent}>
              <h1 className={styles.name}>{profile.name}</h1>
              <p className={styles.role}>{profile.title}</p>
              <p className={styles.loc}>📍 {profile.location}</p>
              <p className={styles.tagline}>{profile.tagline}</p>

              {/* Socials */}
              <div className={styles.socials}>
                {SOCIAL_META.map((s) => (
                  <a
                    key={s.key}
                    href={profile.socials[s.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialBtn}
                  >
                    {s.emoji} {s.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SITE STATS ── */}
      <div className={styles.statsBar}>
        <div className="container">
          <div className={styles.statsGrid}>
            {[
              ["🧁", recipes.length, "Recipes Published"],
              ["💻", devPosts.length, "Dev Posts"],
              ["⚡", devHacks.length, "Dev Hacks"],
              // ['📅', '2023',           'Started Writing'],
            ].map(([e, n, l]) => (
              <div key={l} className={styles.statCard}>
                <span className={styles.statE}>{e}</span>
                <span className={styles.statN}>{n}</span>
                <span className={styles.statL}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container">
        {/* ── BIO ── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>👋 A little about me</h2>
          <div className={styles.bioGrid}>
            {profile.bio.map((para, i) => (
              <p key={i} className={styles.bioPara}>
                {para}
              </p>
            ))}
          </div>
        </section>

        {/* ── SKILLS ── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>🛠 Skills & Specialties</h2>
          <div className={styles.skillsGrid}>
            <div className={styles.skillCard}>
              <h3 className={styles.skillCardTitle}>💻 Tech Stack</h3>
              <div className={styles.skillPills}>
                {profile.techStack.map((s) => (
                  <span key={s} className={`${styles.pill} ${styles.pillTech}`}>
                    {s}
                  </span>
                ))}
              </div>
              <p className={styles.skillDesc}>
                I build full-stack web applications with a strong focus on clean
                React UIs, high performance, and a great DX. I also explore
                UI/UX design in Canva out of curiosity - especially enjoying
                creating aesthetic invites, and like working across the entire
                stack.
              </p>
            </div>

            <div className={styles.skillCard}>
              <h3 className={styles.skillCardTitle}>🧁 Baking Specialties</h3>
              <div className={styles.skillPills}>
                {profile.bakingSpecialties.map((s) => (
                  <span key={s} className={`${styles.pill} ${styles.pillBake}`}>
                    {s}
                  </span>
                ))}
              </div>
              <p className={styles.skillDesc}>
                Classic techniques with unexpected flavour pairings. Every
                recipe on this site has been made at least twice - once to learn
                it, once to perfect it.
              </p>
            </div>
          </div>
        </section>

        {/* ── SERVICES / HIRE ── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>💼 Work With Me</h2>
          <p className={styles.servicesIntro}>
            Available for freelance projects, recipe collaborations and
            technical writing.
          </p>
          <div className={styles.servicesGrid}>
            {profile.services.map((svc) => (
              <div key={svc.title} className={styles.serviceCard}>
                <span className={styles.serviceIcon}>{svc.icon}</span>
                <h3 className={styles.serviceTitle}>{svc.title}</h3>
                <p className={styles.serviceDesc}>{svc.desc}</p>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className={styles.contactCta}>
            <div className={styles.contactCtaLeft}>
              <h3 className={styles.contactCtaTitle}>
                Let's build something together
              </h3>
              <p className={styles.contactCtaSub}>
                Drop me an email or connect on LinkedIn - I reply to every
                message (though I might occasionally miss one).
              </p>
            </div>
            <div className={styles.contactCtaBtns}>
              <a href={`mailto:${profile.email}`} className="btn-primary">
                📩 Email Me
              </a>
              <a
                href={profile.socials.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                💼 LinkedIn
              </a>
            </div>
          </div>
        </section>

        {/* ── EXPLORE ── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>✦ Explore the site</h2>
          <div className={styles.exploreGrid}>
            {[
              {
                emoji: "🧁",
                label: "Baking Recipes",
                sub: `${recipes.length} recipes`,
                to: "/baking",
                bg: "#fff0f8",
              },
              {
                emoji: "💻",
                label: "Dev Blog",
                sub: `${devPosts.length} posts`,
                to: "/dev",
                bg: "#f0f4ff",
              },
              {
                emoji: "⚡",
                label: "Dev Hacks",
                sub: `${devHacks.length} hacks`,
                to: "/hacks",
                bg: "#f0fff4",
              },
            ].map((card) => (
              <Link
                key={card.label}
                to={card.to}
                className={styles.exploreCard}
                style={{ background: card.bg }}
              >
                <span className={styles.exploreEmoji}>{card.emoji}</span>
                <span className={styles.exploreLabel}>{card.label}</span>
                <span className={styles.exploreSub}>{card.sub}</span>
                <span className={styles.exploreArrow}>Go →</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {/* <NewsletterSection /> */}
    </div>
  );
}
